import React, { useState } from 'react';
import useRouter from 'use-react-router';
import { useSelector } from 'react-redux';
import classnames from 'classnames';
import { AngleLeftLightIcon, EditIcon } from '@mm/icons';
import { ReactComponent as ShieldCheckIcon } from 'assets/shield-check.svg';
import { activeChatbotIdSelector } from '@mm/state/selectors/viewSelectors';
import { DeleteButton, Line } from '@mm/components';
import ConnectionModel from '@mm/state/model/ConnectionModel';
import { NeedHelpLabel } from '../../../../../../common/components/NeedHelpLabel/NeedHelpLabel';
import { BotBuilderRoutes } from '../../../../../../../../routes/bot-builder-routes';
import {
  ButtonColor,
  PrimaryButton,
} from '../../../../../../../../common/buttons/PrimaryButton/PrimaryButton';
import TextInput from '../../../../../../../../common/components/TextInput';
import { SwitchStatus } from '../../../../../../common/components/SwitchStatus/SwitchStatus';
import './EditNativeIntegrationHeader.pcss';
import {
  canBeTested,
  canBeTestedMailchimp,
} from '../../../../../../connections/panels/EditConnectionPanel/helpers';

type Props = {
  item: ConnectionModel;
  onToggleActive: () => void;
  onClickDelete: () => void;
  onClickTest: () => void;
  onNameUpdate: (name: string) => void;
  helpLink?: string;
  isSlideout?: boolean;
  reloadConnections?: () => void;
  onBackClick?: () => void;
};

const IntegrationHeaderRightSide = ({
  helpLink,
  onClickTest,
  item,
  onToggleActive,
  isSlideout,
  onClickDelete,
}: Props) => {
  let disabled = true;

  if (item.mailchimpAccessTokenId) {
    disabled = !canBeTestedMailchimp(item, true);
  } else if (item.googleAccountId) {
    disabled = !canBeTested(item, true);
  }
  return (
    <div className="edit-native-integrations-header__title-area-right">
      {helpLink && <NeedHelpLabel link={helpLink} />}

      <PrimaryButton
        color={ButtonColor.secondaryBlue}
        className="edit-native-integrations-header__preview-button"
        onClick={onClickTest}
        disabled={disabled}
      >
        Test Connection
      </PrimaryButton>

      <SwitchStatus
        active={item.active}
        disabled={disabled}
        onChange={onToggleActive}
      />
      {!isSlideout && (
        <DeleteButton
          onClick={onClickDelete}
          className="edit-native-integrations-header__delete-icon"
        />
      )}
    </div>
  );
};

export const EditNativeIntegrationHeader = (props: Props) => {
  const { item, onNameUpdate, isSlideout, reloadConnections, onBackClick } =
    props;

  const chatbotId = useSelector(activeChatbotIdSelector);
  const { history } = useRouter();
  const [nameEditMode, setNameEditMode] = useState(false);
  const [localName, setLocalName] = useState(item.name);

  const classList = classnames('edit-native-integrations-header__name-input', {
    'edit-native-integrations-header--name-disabled-mode': !nameEditMode,
  });

  const saveButtonClassList = classnames(
    'edit-native-integrations-header__name-save-button',
    {
      'edit-native-integrations-header--save-disabled': !localName,
    },
  );

  const onClickCancelSaveName = () => {
    setLocalName(item.name);
    setNameEditMode(false);
  };

  const onClickSaveName = () => {
    onNameUpdate(localName);
    setNameEditMode(false);
    if (isSlideout && reloadConnections) {
      reloadConnections();
    }
  };

  return (
    <div className={classnames('edit-native-integrations-header')}>
      <div className="edit-native-integrations-header__title-area-container">
        {((onBackClick && isSlideout) || !isSlideout) && (
          <div
            className="edit-native-integrations-header__back-button"
            onClick={() =>
              onBackClick
                ? onBackClick()
                : history.push(
                    BotBuilderRoutes.nativeIntegrations.getLink({
                      chatbotId,
                    }),
                  )
            }
          >
            <AngleLeftLightIcon />
            Back
          </div>
        )}
        <div className="edit-native-integrations-header__title-area">
          <div className="edit-native-integrations-header__title-area-left">
            {nameEditMode ? (
              <TextInput
                value={localName}
                onChange={e => setLocalName(e.target.value)}
                disabled={!nameEditMode}
                className={classList}
                maxLength={50}
              />
            ) : (
              <div className="edit-native-integrations-header__name-preview">
                {localName}
              </div>
            )}

            {!nameEditMode && (
              <EditIcon
                className="edit-native-integrations-header__edit-icon"
                onClick={() => setNameEditMode(true)}
              />
            )}
            {nameEditMode && (
              <>
                <PrimaryButton
                  color={ButtonColor.secondaryBlue}
                  onClick={onClickCancelSaveName}
                  className="edit-native-integrations-header__name-save-button"
                >
                  Cancel
                </PrimaryButton>

                <PrimaryButton
                  onClick={onClickSaveName}
                  className={saveButtonClassList}
                  disabled={!localName.trim().length}
                >
                  Save
                </PrimaryButton>
              </>
            )}
          </div>
          {!isSlideout && <IntegrationHeaderRightSide {...props} />}
        </div>
      </div>
      {isSlideout && (
        <>
          <div className="edit-native-integrations-header__subheader">
            <div className="edit-native-integrations-header__note">
              <ShieldCheckIcon />
              All changes are saved automatically
            </div>
            <IntegrationHeaderRightSide {...props} />
          </div>
        </>
      )}
    </div>
  );
};
